/**
 * Change links in an assistant reply — the C-ids of the edits the agent made
 * with ``doc_propose_changes``.
 *
 *   - ``ChangeChip``: a C-id in the reply's text. Click jumps Word to the
 *     change and opens a popover with its diff, status and Undo / Redo.
 *   - ``ChangeSetBar``: under the reply — the set's tally and Undo all /
 *     Redo all.
 *   - ``linkifyChangeIds``: turns bare "C3" mentions (tables included) into
 *     ``asthra-change:`` links the bubble renders as chips.
 */

import { Popover, PopoverSurface, PopoverTrigger, makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import {
  type ChatChange,
  changeLiveState,
  redoChanges,
  revealChange,
  undoChanges,
  useChangeStateVersion,
} from "../services/chatChanges";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";
import { OP_LABEL } from "./ChangeSetApprovalMessage";
import { DiffView } from "./DiffView";

export const CHANGE_LINK_PREFIX = "asthra-change:";

/** Link bare change ids ("C3") the reply mentions to their change. Ids
 *  already inside a link, a URL or a longer word are left alone. */
export function linkifyChangeIds(text: string, changes: ChatChange[]): string {
  if (changes.length === 0) return text;
  const ids = new Set(changes.map((c) => c.id));
  return text.replace(/(?<![[\w:/-])(C\d{1,3})(?![\w\]])/g, (m) => (ids.has(m) ? `[${m}](${CHANGE_LINK_PREFIX}${m})` : m));
}

type Shown = "applied" | "undone" | "rejected" | "not_found" | "failed" | "pending";

function shownStatus(scope: string, change: ChatChange): Shown {
  if (change.status === "applied") return changeLiveState(scope, change).undone ? "undone" : "applied";
  return change.status;
}

const STATUS_LABEL: Record<Shown, string> = {
  applied: "Applied",
  undone: "Undone",
  rejected: "Not approved",
  not_found: "Text not found",
  failed: "Failed",
  pending: "Pending",
};

/** Plain words for the locate / apply codes a change can come back with —
 *  the raw code ("InvalidArgument", "not_found") means nothing to a writer. */
const ERROR_TEXT: Record<string, string> = {
  not_found: "The quoted text isn't in that section.",
  needle_absent: "The quoted text isn't in that section.",
  ambiguous: "The quoted text appears more than once in that section.",
  table_conflict: "This text is in a table; table cells are edited separately.",
  no_section: "There is no section with that name in the document.",
  no_heading: "There is no section with that name in the document.",
  InvalidArgument: "There is no section with that name in the document.",
  empty_insert: "There was no text to insert.",
};

export function changeErrorText(error: string): string {
  const key = Object.keys(ERROR_TEXT).find((k) => error === k || error.startsWith(`${k}:`) || error.includes(k));
  return key ? ERROR_TEXT[key] : error;
}

const STATUS_COLOR: Record<Shown, string> = {
  applied: "var(--success)",
  undone: inkColors.ink4,
  rejected: inkColors.ink4,
  not_found: statusColors.warn,
  failed: statusColors.danger,
  pending: inkColors.ink4,
};

const useStyles = makeStyles({
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    fontFamily: fonts.mono,
    fontSize: "0.88em",
    fontWeight: 600,
    lineHeight: 1.3,
    padding: "0 5px",
    borderRadius: "4px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    background: brandColors.tint,
    color: brandColors.primary,
    cursor: "pointer",
    verticalAlign: "baseline",
    ":hover": { background: brandColors.light },
  },
  dot: { width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0 },
  surface: { width: "300px", maxWidth: "calc(100vw - 16px)", boxSizing: "border-box", padding: "10px 12px", fontFamily: fonts.body },
  head: { display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px", fontSize: "11.5px" },
  id: { fontFamily: fonts.mono, fontWeight: 600, color: brandColors.primary },
  meta: { fontFamily: fonts.mono, fontSize: "10.5px", color: inkColors.ink4 },
  status: { marginLeft: "auto", fontSize: "10.5px", fontWeight: 600 },
  rationale: { fontSize: "11.5px", color: inkColors.ink3, marginBottom: "6px" },
  error: { fontSize: "11px", color: "var(--danger)", marginTop: "6px" },
  row: { display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "8px" },
  btn: {
    borderRadius: "6px",
    padding: "3px 10px",
    fontSize: "11.5px",
    fontWeight: 600,
    cursor: "pointer",
    background: "var(--surface-raised)",
    color: inkColors.ink2,
    border: `1px solid ${surfaceColors.lineStrong}`,
    ":disabled": { opacity: 0.55, cursor: "default" },
  },
  bar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px",
    marginTop: "8px",
    paddingTop: "8px",
    borderTop: `1px solid ${surfaceColors.line}`,
    fontSize: "11.5px",
    color: inkColors.ink3,
  },
  barTally: { marginRight: "auto" },
});

/** Undo / Redo for a list of changes; shared by the chip and the bar. */
function useUndoRedo(scope: string, changes: ChatChange[]) {
  useChangeStateVersion();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const applied = changes.filter((c) => c.status === "applied");
  const undoable = applied.filter((c) => !changeLiveState(scope, c).undone);
  const redoable = applied.filter((c) => changeLiveState(scope, c).undone);
  const run = async (kind: "undo" | "redo") => {
    setBusy(true);
    setNote(null);
    try {
      const out = kind === "undo" ? await undoChanges(scope, changes) : await redoChanges(scope, changes);
      if (out.failed.length > 0) {
        setNote(
          `Couldn't ${kind} ${out.failed.map((f) => f.id).join(", ")}: ${out.failed[0].error}. The text may have changed since.`,
        );
      }
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, note, undoable, redoable, run };
}

export const ChangeChip: React.FC<{ change: ChatChange; scope: string; label?: React.ReactNode }> = ({
  change,
  scope,
  label,
}) => {
  const styles = useStyles();
  const { busy, note, undoable, redoable, run } = useUndoRedo(scope, [change]);
  const shown = shownStatus(scope, change);
  const [open, setOpen] = useState(false);
  const undone = shown === "undone";

  return (
    <Popover
      open={open}
      onOpenChange={(_, data) => setOpen(data.open)}
      // Keep the card inside the pane: a chip in the middle of the bar
      // otherwise pushed a 300 px card past the right edge.
      positioning={{ position: "below", align: "start", overflowBoundary: "window", overflowBoundaryPadding: 8 }}
      withArrow
      trapFocus={false}
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={styles.chip}
          title={`${change.id} · §${change.section} — show in Word`}
          onClick={() => {
            void revealChange(scope, change);
          }}
        >
          <span className={styles.dot} style={{ background: STATUS_COLOR[shown] }} />
          {label ?? change.id}
        </button>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface} aria-label={`Change ${change.id}`}>
        <div className={styles.head}>
          <span className={styles.id}>{change.id}</span>
          <span className={styles.meta}>
            §{change.section} · {OP_LABEL[change.op] ?? change.op}
          </span>
          <span className={styles.status} style={{ color: STATUS_COLOR[shown] }}>
            {STATUS_LABEL[shown]}
          </span>
        </div>
        {change.rationale ? <div className={styles.rationale}>{change.rationale}</div> : null}
        {/* Undone: the document holds the original again — show it that way. */}
        <DiffView
          before={undone ? change.after : change.before}
          after={undone ? change.before : change.after}
          maxHeight={180}
          hideSummary
        />
        {change.status !== "applied" && change.error ? <div className={styles.error}>{changeErrorText(change.error)}</div> : null}
        {note ? <div className={styles.error}>{note}</div> : null}
        <div className={styles.row}>
          <button type="button" className={styles.btn} onClick={() => void revealChange(scope, change)}>
            Show in Word
          </button>
          {undoable.length > 0 && (
            <button type="button" className={styles.btn} disabled={busy} onClick={() => void run("undo")}>
              Undo
            </button>
          )}
          {redoable.length > 0 && (
            <button type="button" className={styles.btn} disabled={busy} onClick={() => void run("redo")}>
              Redo
            </button>
          )}
        </div>
      </PopoverSurface>
    </Popover>
  );
};

export const ChangeSetBar: React.FC<{ changes: ChatChange[]; scope: string }> = ({ changes, scope }) => {
  const styles = useStyles();
  const { busy, note, undoable, redoable, run } = useUndoRedo(scope, changes);
  const counts = new Map<Shown, number>();
  for (const c of changes) {
    const s = shownStatus(scope, c);
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  const tally = [...counts].map(([s, n]) => `${n} ${STATUS_LABEL[s].toLowerCase()}`).join(" · ");

  return (
    <div className={styles.bar}>
      <span className={styles.barTally}>
        {changes.map((c) => (
          <span key={c.id} style={{ marginRight: "4px" }}>
            <ChangeChip change={c} scope={scope} />
          </span>
        ))}
        <span>{tally}</span>
      </span>
      <button
        type="button"
        className={mergeClasses(styles.btn)}
        disabled={busy || undoable.length === 0}
        onClick={() => void run("undo")}
        title="Put the original text back for every applied change"
      >
        {busy ? "Working…" : "Undo"}
      </button>
      <button
        type="button"
        className={styles.btn}
        disabled={busy || redoable.length === 0}
        onClick={() => void run("redo")}
        title="Apply the undone changes again"
      >
        Redo
      </button>
      {note ? <div className={styles.error} style={{ flexBasis: "100%" }}>{note}</div> : null}
    </div>
  );
};
